import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle,
  X,
  ArrowRight,
  ArrowLeft,
  ShieldAlert,
  CheckCircle2,
  GraduationCap,
  Briefcase,
  BookOpen,
  Package,
  CalendarCheck,
  HeartPulse,
  Users,
  Send,
  Phone,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

/**
 * Public guided chatbox.
 * - Structured quick actions only (no free-form AI)
 * - General Question submits into existing inbox_messages with source="chatbox"
 * - Mounted only inside PublicLayout — never on /care-portal/* or admin pages
 */

const MAIN_OPTIONS = [
  {
    id: "chaplaincy_services",
    label: "Chaplaincy Services",
    icon: Users,
    followups: [
      { label: "Workplace Chaplaincy", route: "/services/workplace-chaplaincy" },
      { label: "Crisis, Grief & Bereavement", route: "/services/crisis-grief-bereavement" },
    ],
  },
  {
    id: "healthcare_care_settings",
    label: "Healthcare & Care Settings",
    icon: HeartPulse,
    followups: [
      { label: "Healthcare & Hospice", route: "/services/healthcare-hospice" },
      { label: "Senior Living / Board & Care", route: "/services/senior-living" },
    ],
  },
  {
    id: "industries",
    label: "Industries We Serve",
    icon: Building2,
    followups: [
      { label: "Automotive & Dealerships", route: "/industries/automotive-dealerships" },
      { label: "Restaurants, Fast-Food Franchises & Hospitality", route: "/industries/restaurants-hospitality" },
      { label: "Construction & Skilled Trades", route: "/industries/construction-skilled-trades" },
      { label: "Real Estate, Brokerages & Title Services", route: "/industries/real-estate-brokerages-title-services" },
    ],
  },
  {
    id: "for_organizations",
    label: "For Organizations",
    icon: Briefcase,
    followups: [
      { label: "Leadership / Employee Support", route: "/services/leadership-employee-support" },
      { label: "Training & Workshops", route: "/services/training-workshops" },
      { label: "Care Team Packages", route: "/packages" },
      { label: "Books & Organizational Resources", route: "/books" },
      { label: "Frequently Asked Questions", route: "/faq" },
      { label: "For Organizations overview", route: "/for-organizations" },
    ],
  },
  {
    id: "callachap",
    label: "CALLACHAP",
    icon: Phone,
    description: "CCCHAPS' employee-facing option for connecting with chaplain support by call, text, confidential message, or remote support.",
    followups: [
      { label: "Explore CALLACHAP (concept demo)", route: "/callachap" },
      { label: "About CCCHAPS Workplace Chaplaincy", route: "/services/workplace-chaplaincy" },
    ],
  },
  {
    id: "blog",
    label: "Blog",
    icon: BookOpen,
    followups: [
      { label: "Articles & Insights", route: "/resources/articles" },
      { label: "Free Workforce Resources", route: "/resources" },
      { label: "Compare Chaplaincy and EAP", route: "/resources/chaplaincy-vs-eap" },
    ],
  },
  { id: "contact", label: "Contact CCCHAPS", icon: Send, route: "/contact" },
  { groupHeader: "More options" },
  {
    id: "chaplain_training_careers",
    label: "Chaplain Training & Careers",
    icon: GraduationCap,
    badge: "Applications Now Open",
    followups: [
      { label: "Chaplain Training Academy", route: "/academy" },
      { label: "Apply to the Academy", route: "/academy/apply" },
      { label: "Careers at CCCHAPS", route: "/careers" },
    ],
  },
  { id: "bulk_orders", label: "Bulk Orders", icon: Package, route: "/bulk-order" },
  { id: "schedule", label: "Schedule a Consultation", icon: CalendarCheck, route: "/schedule" },
  { id: "care_portal", label: "Care Portal (enrolled employees)", icon: ShieldAlert, route: "/care-portal" },
  { id: "general_question", label: "General Question", icon: MessageCircle, view: "form" },
  { id: "emergency", label: "Emergency or in danger", icon: ShieldAlert, view: "emergency" },
];

export default function PublicChatbox() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // menu | followups | form | emergency | success
  const [activeOption, setActiveOption] = useState(null);
  const launcherRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const navigate = useNavigate();

  const reset = useCallback(() => {
    setView("menu");
    setActiveOption(null);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => launcherRef.current?.focus(), 0);
  }, []);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Focus first focusable on open / view change
  useEffect(() => {
    if (open) setTimeout(() => firstFocusableRef.current?.focus(), 30);
  }, [open, view]);

  const handleMainSelect = (opt) => {
    if (opt.route) {
      close();
      navigate(opt.route);
      setTimeout(reset, 300);
      return;
    }
    if (opt.view === "form") {
      setActiveOption(opt);
      setView("form");
      return;
    }
    if (opt.view === "emergency") {
      setActiveOption(opt);
      setView("emergency");
      return;
    }
    if (opt.followups) {
      setActiveOption(opt);
      setView("followups");
    }
  };

  const handleFollowupSelect = (fu) => {
    close();
    navigate(fu.route);
    setTimeout(reset, 300);
  };

  return (
    <>
      {/* Launcher */}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="ccchaps-chatbox-panel"
        data-testid="chatbox-launcher"
        className={`fixed z-[60] bottom-5 right-5 md:bottom-6 md:right-6 inline-flex items-center gap-2 rounded-full bg-[#4A6FA5] text-white shadow-lg shadow-slate-900/10 px-5 py-3 text-sm font-medium hover:bg-[#3B597B] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A6FA5] transition-transform ${
          open ? "scale-0 opacity-0 pointer-events-none" : "scale-100 opacity-100"
        }`}
      >
        <MessageCircle className="w-4 h-4" strokeWidth={2} />
        How can we help?
      </button>

      {/* Panel */}
      {open && (
        <div
          id="ccchaps-chatbox-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="ccchaps-chatbox-title"
          data-testid="chatbox-panel"
          className="fixed z-[70] inset-x-3 bottom-3 md:bottom-6 md:right-6 md:inset-x-auto md:w-[380px] max-h-[calc(100vh-2rem)] md:max-h-[640px] rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/15 flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between gap-2 px-4 py-3 bg-[#4A6FA5] text-white shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {view !== "menu" && (
                <button
                  type="button"
                  onClick={reset}
                  aria-label="Back to main menu"
                  data-testid="chatbox-back"
                  className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div className="min-w-0">
                <p id="ccchaps-chatbox-title" className="font-heading text-sm font-semibold truncate">
                  How can we help?
                </p>
                <p className="text-[11px] text-white/80 truncate">CCCHAPS · guided assistance</p>
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close chatbox"
              data-testid="chatbox-close"
              className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-4">
            {view === "menu" && <MenuView firstRef={firstFocusableRef} onSelect={handleMainSelect} />}
            {view === "followups" && (
              <FollowupsView firstRef={firstFocusableRef} option={activeOption} onSelect={handleFollowupSelect} />
            )}
            {view === "form" && (
              <GeneralQuestionForm firstRef={firstFocusableRef} onSuccess={() => setView("success")} />
            )}
            {view === "emergency" && <EmergencyView firstRef={firstFocusableRef} onBack={reset} />}
            {view === "success" && <SuccessView firstRef={firstFocusableRef} onDone={close} />}
          </div>

          <footer className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 text-[10.5px] leading-snug text-slate-500 shrink-0">
            This chatbox is not an emergency service or a mental-health crisis line. In an emergency, call 911.
          </footer>
        </div>
      )}
    </>
  );
}

function MenuView({ firstRef, onSelect }) {
  // Track index of the first selectable option so we can attach the initial focus ref to it.
  const firstSelectableIndex = MAIN_OPTIONS.findIndex((o) => !o.groupHeader);
  return (
    <div data-testid="chatbox-menu">
      <p className="text-sm text-slate-700 leading-relaxed mb-3">
        Choose a topic and we'll take you to the right place — or send us a general question.
      </p>
      <ul className="space-y-1.5">
        {MAIN_OPTIONS.map((opt, i) =>
          opt.groupHeader ? (
            <li
              key={`chatbox-group-${i}`}
              data-testid={`chatbox-group-${opt.groupHeader.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="pt-2 mt-1 border-t border-slate-100"
              aria-hidden="true"
            >
              <p className="px-1 pt-1 pb-0.5 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                {opt.groupHeader}
              </p>
            </li>
          ) : (
            <li key={opt.id}>
              <button
                ref={i === firstSelectableIndex ? firstRef : null}
                type="button"
                onClick={() => onSelect(opt)}
                data-testid={`chatbox-option-${opt.id}`}
                className={`w-full flex items-start gap-3 text-left rounded-lg border px-3 py-2.5 text-sm hover:border-[#4A6FA5] hover:bg-[#F0F4F8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6FA5] ${
                  opt.id === "emergency"
                    ? "border-amber-200 bg-amber-50/40 text-amber-900"
                    : "border-slate-200 bg-white text-slate-800"
                }`}
              >
                <span className={`shrink-0 mt-0.5 ${opt.id === "emergency" ? "text-amber-700" : "text-[#4A6FA5]"}`}>
                  <opt.icon className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium">{opt.label}</span>
                  {opt.description && (
                    <span className="mt-0.5 block text-[11.5px] text-slate-500 leading-snug">{opt.description}</span>
                  )}
                  {opt.badge && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] font-semibold">
                      {opt.badge}
                    </span>
                  )}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              </button>
            </li>
          )
        )}
      </ul>
    </div>
  );
}

function FollowupsView({ firstRef, option, onSelect }) {
  if (!option) return null;
  return (
    <div data-testid="chatbox-followups">
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">{option.label}</p>
      <p className="text-sm text-slate-700 leading-relaxed mb-3">Pick where you'd like to go next.</p>
      <ul className="space-y-1.5">
        {option.followups.map((fu, i) => (
          <li key={fu.route}>
            <button
              ref={i === 0 ? firstRef : null}
              type="button"
              onClick={() => onSelect(fu)}
              data-testid={`chatbox-followup-${fu.route.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 hover:border-[#4A6FA5] hover:bg-[#F0F4F8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6FA5]"
            >
              <span className="font-medium">{fu.label}</span>
              <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmergencyView({ firstRef, onBack }) {
  return (
    <div data-testid="chatbox-emergency">
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-amber-200 text-amber-700 shrink-0">
            <ShieldAlert className="w-5 h-5" />
          </span>
          <div>
            <p className="font-heading text-sm font-semibold text-amber-900">If this is an emergency</p>
            <p className="mt-1.5 text-sm text-amber-900 leading-relaxed">
              If you or someone else is in immediate danger, call <strong>911</strong> or your local emergency service.
            </p>
          </div>
        </div>
      </div>
      <p className="mt-4 text-sm text-slate-700 leading-relaxed">
        The CCCHAPS chatbox is not an emergency service, a mental-health crisis line, or a therapy resource. We cannot provide clinical diagnosis, therapy, medical advice, legal advice, or emergency counseling here.
      </p>
      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
        For confidential employee chaplain support at an enrolled organization, use the Care Portal.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <a
          ref={firstRef}
          href="tel:911"
          data-testid="chatbox-call-911"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 text-white text-sm font-medium px-4 py-2.5 hover:bg-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-600"
        >
          Call 911
        </a>
        <a
          href="/care-portal"
          data-testid="chatbox-emergency-care-portal"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium px-4 py-2.5 hover:border-[#4A6FA5] hover:text-[#4A6FA5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6FA5]"
        >
          Go to Care Portal
        </a>
        <button
          type="button"
          onClick={onBack}
          data-testid="chatbox-emergency-back"
          className="inline-flex items-center justify-center text-sm text-slate-500 hover:text-[#4A6FA5] mt-1 focus:outline-none focus-visible:underline"
        >
          Back to main menu
        </button>
      </div>
    </div>
  );
}

function SuccessView({ firstRef, onDone }) {
  return (
    <div data-testid="chatbox-success" className="text-center py-4">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-green-50 text-green-600 mb-3">
        <CheckCircle2 className="w-5 h-5" />
      </div>
      <h3 className="font-heading text-base text-slate-900">Thanks — your message has been sent.</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
        A member of the CCCHAPS team will follow up. If your matter is urgent, please call or use one of the guided pathways.
      </p>
      <button
        ref={firstRef}
        type="button"
        onClick={onDone}
        data-testid="chatbox-success-close"
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium px-4 py-2.5 hover:bg-[#3B597B] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A6FA5]"
      >
        Close
      </button>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";

function GeneralQuestionForm({ firstRef, onSuccess }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    organization: "",
    message: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return toast.error("Please enter your first name.");
    if (!form.email.trim()) return toast.error("Please enter your email.");
    if (!form.message.trim() || form.message.trim().length < 3) return toast.error("Please enter a short message.");
    if (!consent) return toast.error("Please acknowledge the consent statement before sending.");
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        organization: form.organization.trim() || undefined,
        subject: "General Question (Chatbox)",
        message: form.message.trim(),
        source: "chatbox",
      };
      await api.post("/inbox", payload);
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="chatbox-form" onSubmit={submit} className="space-y-3">
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">General Question</p>
      <p className="text-sm text-slate-700 leading-relaxed">Send us a short message and we'll follow up.</p>

      <div
        className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-[12px] leading-snug text-amber-900"
        data-testid="chatbox-sensitive-warning"
      >
        Please do not include medical information, diagnoses, Social Security numbers, financial information, or other highly sensitive personal information in this message.
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="First name" required>
          <input
            ref={firstRef}
            required
            value={form.first_name}
            onChange={update("first_name")}
            data-testid="chatbox-first-name"
            className={inputCls}
          />
        </Field>
        <Field label="Last name">
          <input
            value={form.last_name}
            onChange={update("last_name")}
            data-testid="chatbox-last-name"
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Email" required>
        <input
          type="email"
          required
          value={form.email}
          onChange={update("email")}
          data-testid="chatbox-email"
          className={inputCls}
        />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Phone">
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            data-testid="chatbox-phone"
            className={inputCls}
          />
        </Field>
        <Field label="Organization">
          <input
            value={form.organization}
            onChange={update("organization")}
            data-testid="chatbox-organization"
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Message" required>
        <textarea
          rows={4}
          required
          value={form.message}
          onChange={update("message")}
          data-testid="chatbox-message"
          className={`${inputCls} resize-none`}
        />
      </Field>

      <label className="flex items-start gap-2 text-[12px] text-slate-700 pt-1">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="chatbox-consent"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
        />
        <span>I acknowledge this message is being submitted to California Corporate Chaplains for follow-up.</span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        data-testid="chatbox-submit"
        className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-[#4A6FA5] text-white text-sm font-medium px-4 py-2.5 hover:bg-[#3B597B] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A6FA5] disabled:opacity-60"
      >
        {submitting ? "Sending…" : (
          <>
            <Send className="w-4 h-4" /> Send message
          </>
        )}
      </button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-[#4A6FA5]">*</span>}
      </span>
      {children}
    </label>
  );
}
